import { useEffect, useRef, useState } from "react";
import type { UsbConfig } from "../scenario/types";
import { audio } from "../lib/audio";

/**
 * The reveal: a close-up of the back of the PC as the visitor slips a small
 * device inline between the keyboard plug and the USB port — a hardware
 * keylogger — then says it's all done and leaves. Rendered as a DOM cinematic
 * (a clean SVG insertion) because a photoreal hand/USB is not worth faking in
 * the procedural 3D; the 3D scene holds the matching tower + dongle behind it.
 */
export function UsbReveal({
  config,
  onContinue,
}: {
  config: UsbConfig;
  onContinue: () => void;
}) {
  const [seated, setSeated] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    if (config.line) void audio.prefetchSpeech(config.line, { voice: config.voiceId ?? "onyx", lang: config.language ?? "en-US" });
    if (ran.current) return;
    ran.current = true;
    // the dongle slides in over ~1.5s; click as it seats, then the closing line.
    const t1 = window.setTimeout(() => {
      audio.click();
      setSeated(true);
      audio.speak(config.line, { voice: config.voiceId ?? "onyx", lang: config.language ?? "en-US" });
    }, 1500);
    return () => window.clearTimeout(t1);
  }, [config]);

  return (
    <div className="usbreveal">
      <div className="usb-stage">
        <div className="usb-kicker">{config.caption ?? "At the back of your PC…"}</div>

        <div className="usb-art-wrap">
          <svg className="usb-art" viewBox="0 0 520 260" role="img" aria-label="A device is plugged into the PC">
            {/* lit spotlight behind the hardware so it reads against the dim room */}
            <defs>
              <radialGradient id="usbSpot" cx="46%" cy="52%" r="62%">
                <stop offset="0%" stopColor="#aeb9c6" stopOpacity="0.38" />
                <stop offset="55%" stopColor="#5a6471" stopOpacity="0.16" />
                <stop offset="100%" stopColor="#0a0e12" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect x="0" y="0" width="520" height="260" fill="url(#usbSpot)" />

            {/* PC chassis edge (lighter so the dark ports/dongle pop) */}
            <rect x="20" y="40" width="150" height="200" rx="10" fill="#5b6573" stroke="#cdd6df" strokeWidth="3" />
            <rect x="34" y="58" width="122" height="164" rx="6" fill="#444c58" />
            {/* USB port block on the chassis */}
            <rect x="150" y="120" width="46" height="44" rx="4" fill="#11161c" stroke="#9aa6b1" strokeWidth="2" />
            <rect x="158" y="132" width="30" height="8" rx="1.5" fill="#05080a" />
            <rect x="158" y="146" width="30" height="8" rx="1.5" fill="#05080a" />
            {/* keyboard cable coming from the right */}
            <path
              className="usb-cable"
              d="M500 142 C 430 142, 410 142, 372 142"
              fill="none"
              stroke="#717c8a"
              strokeWidth="10"
              strokeLinecap="round"
            />
            {/* the keylogger dongle + keyboard plug, sliding in */}
            <g className={`usb-dongle ${seated ? "seated" : ""}`}>
              {/* pulsing highlight ring around the threat */}
              <rect className="usb-ring" x="188" y="110" width="74" height="64" rx="9" fill="none" stroke="#ff5a6a" strokeWidth="2.5" />
              {/* inline keylogger body (red-outlined = the threat) */}
              <rect x="196" y="118" width="58" height="48" rx="5" fill="#2c333d" stroke="#ff6b79" strokeWidth="2.5" />
              <circle className="usb-rec" cx="208" cy="142" r="4.5" fill="#ff5a6a" />
              {/* the keyboard's own USB plug stacked into the dongle */}
              <rect x="254" y="126" width="40" height="32" rx="4" fill="#48505c" stroke="#aab4bf" strokeWidth="2" />
              <rect x="294" y="136" width="80" height="12" rx="3" fill="#5b6573" />
            </g>
            {/* a hand hint pushing the plug */}
            <g className={`usb-hand ${seated ? "done" : ""}`} opacity="0.75">
              <path d="M470 120 q 30 24 0 48 q -26 6 -40 -4 l 0 -40 q 16 -10 40 -4 Z" fill="#e0c0a4" />
            </g>
            {/* callout label, appears once seated */}
            <g className={`usb-label ${seated ? "show" : ""}`}>
              <line x1="225" y1="104" x2="225" y2="74" stroke="#ff6b79" strokeWidth="1.5" />
              <rect x="120" y="50" width="210" height="24" rx="6" fill="#1b0e11" stroke="#ff5a6a" strokeWidth="1.5" />
              <text x="225" y="66" textAnchor="middle" fill="#ffd7db" fontSize="13" fontWeight="700" letterSpacing="1">
                HARDWARE KEYLOGGER
              </text>
            </g>
          </svg>
        </div>

        <div className={`usb-line ${seated ? "show" : ""}`}>
          <p>“{config.line}”</p>
          <button type="button" className="btn usb-continue" onClick={onContinue}>
            {config.continueLabel ?? "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
